import classNames from 'classnames';
import React from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router';
import { notNil } from '../../core/helpers/is-nil';
import { useAccentBgContext } from '../accent-bg-context/accent-bg-context';
import { Icon } from '../icon/icon';
import { IconVariant } from '../icon/icon-data';
import { Typography } from '../typography/typography';
import styles from './link.module.scss';

const getFontVariant = (variant: LinkProps['variant']) => {
  let fontVariant: FontVariant = 'buttontext';
  if (variant === 'smalltext') {
    fontVariant = 'smalltext';
  } else if (variant === 'footer') {
    fontVariant = 'maintext';
  }
  return fontVariant;
};

type IconConfig = { hasIcon: boolean; variant: IconVariant; iconOnRightSide: boolean };

type FontVariant = 'buttontext' | 'smalltext' | 'maintext';

type LinkContentProps = {
  children?: string;
  hasText: boolean;
  iconConfig: IconConfig | undefined;
  isBoldStyle?: boolean;
  fontVariant: FontVariant;
};

const LinkContent: React.FC<LinkContentProps> = ({ iconConfig, children, hasText, isBoldStyle, fontVariant }) => {
  return (
    <>
      {iconConfig?.hasIcon && !iconConfig.iconOnRightSide ? (
        <Icon className={styles.icon} size="24" variant={iconConfig.variant} />
      ) : null}
      {hasText ? (
        <Typography
          className={styles.linkText}
          fontStyle={isBoldStyle ? 'bold' : undefined}
          tag="span"
          variant={fontVariant}
        >
          {children}
        </Typography>
      ) : null}
      {iconConfig?.hasIcon && iconConfig.iconOnRightSide ? (
        <Icon className={styles.icon} size="24" variant={iconConfig.variant} />
      ) : null}
    </>
  );
};

type BaseLinkProps = { children?: string; active?: boolean; disabled?: boolean } & (
  | {
      variant?: 'regular';
      icon?: IconVariant;
      iconPosition?: 'left' | 'right';
    }
  | {
      variant: 'footer';
      icon?: undefined;
      iconPosition?: undefined;
    }
  | {
      variant: 'smalltext';
      icon?: undefined;
      iconPosition?: undefined;
    }
);

type LinkProps = BaseLinkProps & Omit<RouterLinkProps, 'children'>;

export const Link: React.FC<LinkProps> = ({
  variant = 'regular',
  icon,
  iconPosition,
  active = false,
  disabled,
  children,
  className,
  onClick,
  ...rest
}) => {
  const { isAccentBackground } = useAccentBgContext();

  const hasIcon = notNil(icon);
  const hasText = notNil(children);
  const iconOnly = !hasText && hasIcon;
  const textOnly = hasText && !hasIcon;
  const inFooter = variant === 'footer';

  const iconOnRightSide = hasIcon && iconPosition === 'right';
  const isBoldStyle = (inFooter && active) || variant === 'smalltext';
  const fontVariant = getFontVariant(variant);

  const iconConfig: IconConfig | undefined = hasIcon ? { variant: icon, hasIcon, iconOnRightSide } : undefined;

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (disabled) {
      event.preventDefault();
    } else {
      onClick?.(event);
    }
  };

  return (
    <RouterLink
      className={classNames(styles.root, className, {
        [styles.iconOnly]: iconOnly,
        [styles.textOnly]: textOnly && !inFooter,
        [styles.onAccentBg]: isAccentBackground,
        [styles.active]: active,
        [styles.disabled]: disabled,
      })}
      onClick={handleClick}
      {...rest}
    >
      <LinkContent fontVariant={fontVariant} hasText={hasText} iconConfig={iconConfig} isBoldStyle={isBoldStyle}>
        {children}
      </LinkContent>
    </RouterLink>
  );
};

type LinkButtonProps = BaseLinkProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const LinkButton: React.FC<LinkButtonProps> = ({
  variant = 'regular',
  icon,
  iconPosition = 'left',
  active,
  disabled,
  children,
  className,
  ...rest
}) => {
  const { isAccentBackground } = useAccentBgContext();
  const hasIcon = notNil(icon);
  const hasText = notNil(children);
  const iconOnly = !hasText && hasIcon;
  const textOnly = hasText && !hasIcon;
  const inFooter = variant === 'footer';

  const iconOnRightSide = hasIcon && iconPosition === 'right';
  const isBoldStyle = (inFooter && active) || variant === 'smalltext';
  const fontVariant = getFontVariant(variant);

  const iconConfig: IconConfig | undefined = hasIcon ? { variant: icon, hasIcon, iconOnRightSide } : undefined;

  return (
    <button
      disabled={disabled}
      type="button"
      className={classNames(styles.root, className, {
        [styles.iconOnly]: iconOnly,
        [styles.textOnly]: textOnly && !inFooter,
        [styles.onAccentBg]: isAccentBackground,
        [styles.active]: active,
        [styles.disabled]: disabled,
      })}
      {...rest}
    >
      <LinkContent fontVariant={fontVariant} hasText={hasText} iconConfig={iconConfig} isBoldStyle={isBoldStyle}>
        {children}
      </LinkContent>
    </button>
  );
};
